import { useCallback, useMemo } from 'react';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { ProposalProps, ProposalStatus } from '~/data/proposals';
import {
  ProcessAgencyRole,
  useLoggedInProposalAgency,
} from '~/data/proposals/useLoggedInProposalAgency';
import { useProposalUtils } from '~/data/proposals/useProposalUtils';
import { isRoleHR, isRoleHRAdmin } from '~/services/Permissions';

type PropsType = {
  proposal?: ProposalProps;
};
export const useProposalPermissions = (props: PropsType) => {
  const { proposal } = props;

  const { getResponsibleHRProposalAgent } = useProposalUtils();
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const loggedInUserRoles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(loggedInUserRoles);
  const isHR = isRoleHR(loggedInUserRoles);

  const { getAgencyInRole } = useLoggedInProposalAgency({ proposal });

  const { isResponsibleHR, responsibleHRSelectEnabled } = useMemo(() => {
    const responsibleHRApprover = getResponsibleHRProposalAgent(proposal);

    const isResponsibleHR =
      responsibleHRApprover?.userId === loggedInEmployeeId;

    return {
      responsibleHRSelectEnabled: !!responsibleHRApprover && !isResponsibleHR,
      isResponsibleHR,
    };
  }, [getResponsibleHRProposalAgent, loggedInEmployeeId, proposal]);

  const userPermissions = useMemo(() => {
    const permissions = {
      canInitiate: !!getAgencyInRole(ProcessAgencyRole.Initiator) || true,
      canApprove: !!getAgencyInRole(ProcessAgencyRole.Approver),
      canOversee: !!getAgencyInRole(ProcessAgencyRole.Overseer),
      canAdminister: !!getAgencyInRole(ProcessAgencyRole.Administrator),
      canUltimate: !!getAgencyInRole(ProcessAgencyRole.Ultimate),
    };

    return {
      ...permissions,
    };
  }, [getAgencyInRole]);

  const hasProposalState = useCallback(
    (statuses: ProposalStatus[]) =>
      !!proposal?.proposalStatus && statuses.includes(proposal.proposalStatus),
    [proposal],
  );

  const proposalStatePermissions = useMemo(() => {
    const canEdit =
      hasProposalState([ProposalStatus.PROPOSED, ProposalStatus.IN_PROGRESS]) &&
      (userPermissions.canApprove ||
        userPermissions.canOversee ||
        userPermissions.canAdminister);

    const canCreate =
      hasProposalState([ProposalStatus.PROPOSED]) &&
      userPermissions.canInitiate;

    return {
      canEdit,
      canCreate,
    };
  }, [userPermissions, hasProposalState]);

  const userHasApprovedOnce = useMemo(() => {
    return !!proposal?.approvalSteps.find((step) => {
      return step.updatedBy.userId === loggedInEmployeeId;
    });
  }, [proposal, loggedInEmployeeId]);

  const canSubmitForm = useCallback(
    (isFormDirty: boolean) => {
      const { canEdit, canCreate } = proposalStatePermissions;

      if (isFormDirty) {
        return canEdit || canCreate;
      }

      if (!userHasApprovedOnce) {
        return canEdit;
      }

      return false;
    },
    [proposalStatePermissions, userHasApprovedOnce],
  );

  return {
    ...userPermissions,
    ...proposalStatePermissions,
    canSubmitForm,
    responsibleHRSelectEnabled,
    readOnlyMode:
      !proposalStatePermissions.canEdit && !proposalStatePermissions.canCreate,
    userRoles: {
      isHRAdmin,
      isHR,
      isResponsibleHR,
    },
    loggedInEmployeeId,
  };
};
